import {useQueryClient} from '@tanstack/react-query';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {Tracker} from '@udemy/event-tracking';
import {useMatchMedia} from '@udemy/hooks';
import {AppDownloadModal, OnboardingModal} from '@udemy/plan-user-onboarding';
import {useUDData} from '@udemy/ud-data';

import {COURSE_RECOMMENDATIONS_SECTION_ID} from 'src/features/constants';
import {
    UserChecklistName,
    UserChecklistTaskName,
    type OnboardingChecklistQueryVariables,
    useOnboardingChecklistQuery,
    useUserChecklistTaskCompleteMutation,
    useUserChecklistTaskUpdateLastViewedPageMutation,
} from 'src/generated/graphql';
import {
    getChecklistCompletedItemIds,
    getChecklistCompletedLessons,
    getChecklistItemOrder,
    getChecklistSubscriptionStartDateIso,
    getOnboardingModalInitialStep,
    getWatchLessonsCountForChecklist,
    getWatchLessonsUrl,
    hasOnboardingUserChecklistRow,
    isOnboardingChecklistTaskCompleted,
    isWithinChecklistSubscriptionWindow,
} from 'src/lib/onboarding-checklist/checklist';
import {
    consumeSkipSurveyPendingOpenChecklist,
    initializeChecklistAutoOpenOnMount,
    isChecklistFullyComplete,
    resolveDeferredChecklistAutoOpen,
} from 'src/lib/onboarding-checklist/checklist-auto-open';
import {
    APP_DOWNLOAD_URL,
    OPEN_CHECKLIST_QUERY_PARAM,
    PP_CHECKLIST_WATCH_LESSONS_TOTAL,
} from 'src/lib/onboarding-checklist/constants';
import {ChecklistItemId, ChecklistModal} from 'src/lib/onboarding-checklist/modal';

import styles from '../personal-plan-home-page.module.less';

function buildPersonalPlanOnboardingTaskPayload(taskName: UserChecklistTaskName) {
    return {checklistName: UserChecklistName.Onboarding, taskName};
}

function ChecklistModalWithTrackingEnabled(props: React.ComponentProps<typeof ChecklistModal>) {
    const {isOpen} = props;
    const previousIsTrackingEnabledRef = useRef<boolean | undefined>(undefined);

    useEffect(() => {
        if (!Tracker.context) return;
        previousIsTrackingEnabledRef.current = Tracker.context.isTrackingEnabled;
        Tracker.context.isTrackingEnabled = true;

        return () => {
            if (Tracker.context && previousIsTrackingEnabledRef.current !== undefined) {
                Tracker.context.isTrackingEnabled = previousIsTrackingEnabledRef.current;
            }
        };
    }, []);

    useEffect(() => {
        if (isOpen) consumeSkipSurveyPendingOpenChecklist();
    }, [isOpen]);

    return <ChecklistModal {...props} />;
}

interface PersonalPlanChecklistModalsProps {
    onModalOpenStateChange: (isOpen: boolean) => void;
    isPaidSubscriber: boolean;
    subscriptionStartDate: string | null;
}

export function PersonalPlanChecklistModals({
    onModalOpenStateChange,
    isPaidSubscriber,
    subscriptionStartDate: subscriptionStartDateSSR,
}: PersonalPlanChecklistModalsProps): React.ReactElement | null {
    const udData = useUDData();
    const queryClient = useQueryClient();
    const isMobileMax = useMatchMedia('mobile-max');
    const isMobile = !!isMobileMax;
    // Capture presence of ?openChecklist at initial render (before the mount effect strips it).
    // When true, bypass the 14-day visibility window so the checklist can be force-opened by
    // support or tooling even after a user's onboarding window has expired.
    const [forceChecklistByParam] = useState<boolean>(() => {
        if (typeof window === 'undefined') return false;
        return new URLSearchParams(window.location.search).has(OPEN_CHECKLIST_QUERY_PARAM);
    });

    const shouldFetchChecklistData =
        isPaidSubscriber &&
        typeof window !== 'undefined' &&
        !udData.isGlobalMeContextLoading &&
        // Short-circuit using the SSR-provided subscription start date: if the user is outside
        // the 14-day visibility window, skip the checklist query entirely. Falls back to true
        // when the start date is unknown so we don't incorrectly suppress the fetch.
        // When ?openChecklist=1 is present, bypass the window check entirely so the query
        // runs even for users whose onboarding window has expired.
        (forceChecklistByParam || isWithinChecklistSubscriptionWindow(subscriptionStartDateSSR));

    const checklistQueryVariables: OnboardingChecklistQueryVariables = useMemo(
        () =>
            subscriptionStartDateSSR
                ? {pageSize: 20, enrollmentTimeStart: subscriptionStartDateSSR}
                : {pageSize: 20},
        [subscriptionStartDateSSR],
    );
    const onboardingChecklistQueryKey = useMemo(
        () => useOnboardingChecklistQuery.getKey(checklistQueryVariables),
        [checklistQueryVariables],
    );

    const invalidateOnboardingChecklistQuery = useCallback(() => {
        void queryClient.invalidateQueries({queryKey: onboardingChecklistQueryKey});
    }, [queryClient, onboardingChecklistQueryKey]);

    const {
        data: checklistQueryData,
        isLoading: isChecklistQueryLoading,
        isError,
    } = useOnboardingChecklistQuery(checklistQueryVariables, {
        enabled: shouldFetchChecklistData,
        // Checklist data only changes when a user explicitly completes a task (handled via
        // invalidateOnboardingChecklistQuery after mutations). Prevent spurious re-fetches
        // on window focus or component remount, which would hit the checklist gRPC backend
        // on every tab switch or navigation.
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });

    const {mutateAsync: completeUserChecklistTask, isLoading: isCompletingUserChecklistTask} =
        useUserChecklistTaskCompleteMutation({
            // Refetch checklist task state only after a successful complete (not on page-tracking updates).
            onSuccess: invalidateOnboardingChecklistQuery,
        });
    const {mutateAsync: updateUserChecklistLastViewedPage} =
        useUserChecklistTaskUpdateLastViewedPageMutation();

    const isChecklistDataReady =
        shouldFetchChecklistData && !isChecklistQueryLoading && (!!checklistQueryData || isError);

    const completedLessons = useMemo(
        () => getChecklistCompletedLessons(checklistQueryData),
        [checklistQueryData],
    );

    const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false);
    const [isPPOnboardingModalOpen, setIsPPOnboardingModalOpen] = useState(false);
    const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
    const hasOpenedFromUrlRef = useRef(false);
    const hasChecklistAutoOpenTriggerRef = useRef(false);
    const hasAttemptedAutoOpenRef = useRef(false);
    const shouldAutoOpenChecklistRef = useRef(false);

    const completedItemIds = useMemo(
        () =>
            getChecklistCompletedItemIds({
                completedLessons,
                watchLessonsTotal: PP_CHECKLIST_WATCH_LESSONS_TOTAL,
                onboardingChecklistQuery: checklistQueryData,
            }),
        [completedLessons, checklistQueryData],
    );

    const checklistItems = useMemo(() => {
        const watchLessonsUrl = getWatchLessonsUrl(checklistQueryData);
        return {
            [ChecklistItemId.WatchLessons]: {
                href: watchLessonsUrl ?? '#',
                watchedCount: getWatchLessonsCountForChecklist(
                    checklistQueryData,
                    completedLessons.length,
                ),
                preventNavigation: watchLessonsUrl == null,
                ...(watchLessonsUrl == null && {
                    onClick: () => {
                        document
                            .getElementById(COURSE_RECOMMENDATIONS_SECTION_ID)
                            ?.scrollIntoView({behavior: 'smooth'});
                    },
                }),
            },
        };
    }, [checklistQueryData, completedLessons.length]);

    const itemOrder = useMemo(
        () => getChecklistItemOrder(checklistQueryData),
        [checklistQueryData],
    );

    const onboardingModalInitialStep = useMemo(
        () => getOnboardingModalInitialStep(checklistQueryData),
        [checklistQueryData],
    );

    useEffect(() => {
        if (!isPaidSubscriber || typeof window === 'undefined') {
            return;
        }

        const {openedLearnAboutPlanFromUrl, shouldAutoOpenChecklist: shouldAutoOpen} =
            initializeChecklistAutoOpenOnMount();

        if (openedLearnAboutPlanFromUrl) {
            hasOpenedFromUrlRef.current = true;
            setIsChecklistModalOpen(false);
            setIsPPOnboardingModalOpen(true);
            return;
        }

        if (shouldAutoOpen) {
            hasChecklistAutoOpenTriggerRef.current = true;
            shouldAutoOpenChecklistRef.current = true;
        }
    }, [isPaidSubscriber]);

    useEffect(() => {
        if (!isChecklistDataReady || !shouldAutoOpenChecklistRef.current) {
            return;
        }

        shouldAutoOpenChecklistRef.current = false;

        const {shouldOpenExpanded} = resolveDeferredChecklistAutoOpen(completedItemIds.length);

        if (shouldOpenExpanded) {
            setIsChecklistModalOpen(true);
        } else {
            setIsChecklistModalOpen(false);
        }
    }, [isChecklistDataReady, completedItemIds.length]);

    useEffect(() => {
        if (
            !isPaidSubscriber ||
            typeof window === 'undefined' ||
            !isChecklistDataReady ||
            hasAttemptedAutoOpenRef.current ||
            hasOpenedFromUrlRef.current ||
            hasChecklistAutoOpenTriggerRef.current
        ) {
            return;
        }

        const startDateIso = getChecklistSubscriptionStartDateIso(checklistQueryData);
        // Soft exit: enrollment data not yet available; retry when the query updates.
        if (!startDateIso) {
            return;
        }

        // Terminal exit: past the 14-day window — will never qualify.
        if (!isWithinChecklistSubscriptionWindow(startDateIso)) {
            hasAttemptedAutoOpenRef.current = true;
            return;
        }

        // Terminal exit: user has already seen the modal.
        if (
            isOnboardingChecklistTaskCompleted(
                checklistQueryData,
                UserChecklistTaskName.SeeOnboardingModals,
            )
        ) {
            hasAttemptedAutoOpenRef.current = true;
            return;
        }

        // Soft exit: ONBOARDING row not yet created by the backend; retry when it arrives.
        // We must not open the modal without the row because tryCompleteTask would silently
        // bail and the task would never be marked done, causing the modal to re-open.
        if (!hasOnboardingUserChecklistRow(checklistQueryData)) {
            return;
        }

        hasAttemptedAutoOpenRef.current = true;
        setIsPPOnboardingModalOpen(true);
    }, [isPaidSubscriber, isChecklistDataReady, checklistQueryData]);

    useEffect(() => {
        onModalOpenStateChange(
            isPPOnboardingModalOpen || isChecklistModalOpen || isAppDownloadModalOpen,
        );
    }, [
        isPPOnboardingModalOpen,
        isChecklistModalOpen,
        isAppDownloadModalOpen,
        onModalOpenStateChange,
    ]);

    const tryCompleteTask = useCallback(
        async (taskName: UserChecklistTaskName) => {
            if (
                isCompletingUserChecklistTask ||
                !hasOnboardingUserChecklistRow(checklistQueryData) ||
                isOnboardingChecklistTaskCompleted(checklistQueryData, taskName)
            ) {
                return;
            }
            try {
                await completeUserChecklistTask(buildPersonalPlanOnboardingTaskPayload(taskName));
            } catch {
                // Duplicate completes or transient BE errors; caller still proceeds.
            }
        },
        [checklistQueryData, completeUserChecklistTask, isCompletingUserChecklistTask],
    );

    const onPPOnboardingModalClose = useCallback(async () => {
        setIsPPOnboardingModalOpen(false);
        await tryCompleteTask(UserChecklistTaskName.SeeOnboardingModals);
        setIsChecklistModalOpen(true);
    }, [tryCompleteTask]);

    const onOnboardingModalContinue = useCallback(
        (nextStep: number) => {
            if (!hasOnboardingUserChecklistRow(checklistQueryData)) {
                return;
            }
            void updateUserChecklistLastViewedPage({
                ...buildPersonalPlanOnboardingTaskPayload(
                    UserChecklistTaskName.SeeOnboardingModals,
                ),
                lastViewedPageNumber: Math.max(0, nextStep - 1),
            });
            if (nextStep >= 1) {
                void tryCompleteTask(UserChecklistTaskName.SeeOnboardingModals);
            }
        },
        [checklistQueryData, updateUserChecklistLastViewedPage, tryCompleteTask],
    );

    const onChecklistModalClose = useCallback(() => {
        setIsChecklistModalOpen(false);
    }, []);

    const onChecklistModalOpen = useCallback(() => {
        setIsChecklistModalOpen(true);
    }, []);

    const onAppDownloadModalClose = useCallback(() => {
        setIsAppDownloadModalOpen(false);
        setIsChecklistModalOpen(true);
    }, []);

    const onGetAppAfterPersist = useCallback((mobile: boolean) => {
        setIsChecklistModalOpen(false);
        if (mobile && typeof window !== 'undefined') {
            window.open(APP_DOWNLOAD_URL, '_blank', 'noopener,noreferrer');
        } else {
            setIsAppDownloadModalOpen(true);
        }
    }, []);

    const onChecklistItemClick = useCallback(
        async (itemId: ChecklistItemId) => {
            switch (itemId) {
                case ChecklistItemId.LearnPlan:
                    setIsChecklistModalOpen(false);
                    setIsPPOnboardingModalOpen(true);
                    return;
                case ChecklistItemId.CustomizeInterests:
                    await tryCompleteTask(UserChecklistTaskName.UpdateSkillsAndOccupationSurvey);
                    // Navigation to /personalize is handled by the package's href for this item.
                    return;
                case ChecklistItemId.GetApp:
                    await tryCompleteTask(UserChecklistTaskName.DownloadApp);
                    onGetAppAfterPersist(isMobile);
                    return;
                case ChecklistItemId.Assessments:
                    await tryCompleteTask(UserChecklistTaskName.CheckOutAssessments);
                    return;
            }
        },
        [isMobile, tryCompleteTask, onGetAppAfterPersist],
    );

    if (!isPaidSubscriber) return null;

    const subscriptionStartDateForGate = getChecklistSubscriptionStartDateIso(checklistQueryData);
    const showChecklistModal =
        isChecklistDataReady &&
        !isChecklistFullyComplete(completedItemIds.length) &&
        (forceChecklistByParam ||
            isWithinChecklistSubscriptionWindow(subscriptionStartDateForGate));

    return (
        <div className={styles['modals-layer']}>
            <OnboardingModal
                isOpen={isPPOnboardingModalOpen}
                onClose={onPPOnboardingModalClose}
                onContinue={onOnboardingModalContinue}
                initialStep={onboardingModalInitialStep}
            />
            {showChecklistModal ? (
                <ChecklistModalWithTrackingEnabled
                    isOpen={isChecklistModalOpen}
                    onClose={onChecklistModalClose}
                    onOpen={onChecklistModalOpen}
                    onChecklistItemClick={async (id) => {
                        await onChecklistItemClick(id);
                    }}
                    completedItemIds={completedItemIds}
                    items={checklistItems}
                    itemOrder={itemOrder}
                />
            ) : null}
            <AppDownloadModal isOpen={isAppDownloadModalOpen} onClose={onAppDownloadModalClose} />
        </div>
    );
}
